import * as React from "react";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { useAppDispatch } from "../../../store/hooks";
import {
  checkCanReach,
  clearReachError,
  fetchDataItems,
  fetchRegions,
  fetchServices,
  fetchTypes,
  selectRegion,
  selectService,
  selectType,
  setCanReach,
  setCloudaccountLink,
} from "../../../store/slices/browserServiceSlice";

export interface LogsBrowserService {
  page: "none" | "cloudaccount" | "bucket";
  setPage: React.Dispatch<React.SetStateAction<"none" | "cloudaccount" | "bucket">>;
  cloudAccountName: string;
  setCloudAccountName: (value: string) => Promise<void>;
  isCheckingReach: boolean;
  isPreparing: boolean;
  prepareError: string;
  prepare: () => Promise<void>;
}

export function useLogsBrowserService() {
  const dispatch = useAppDispatch();

  const [page, setPage] = React.useState<"none" | "cloudaccount" | "bucket">("none");
  const [cloudAccountName, _setCloudAccountName] = React.useState("");
  const [isCheckingReach, setIsCheckingReach] = React.useState(false);
  const [isPreparing, setIsPreparing] = React.useState(false);
  const [prepareError, setPrepareError] = React.useState("");

  async function prepare() {
    try {
      setIsPreparing(true);
      setPrepareError("");
      const services = await dispatch(fetchServices()).unwrap();

      const service_s3 = services.find((service) => service.href.includes("/s3"));
      if (!service_s3) {
        setIsPreparing(false);
        setPrepareError("No S3 service is found");
        return;
      }
      dispatch(selectService(service_s3.href));

      const regions = await dispatch(fetchRegions()).unwrap();
      const region_global = regions.find((service) => service.href.includes("/global"));
      if (!region_global) {
        setIsPreparing(false);
        setPrepareError("No global region is found");
        return;
      }
      dispatch(selectRegion(region_global.href));
      const types = await dispatch(fetchTypes()).unwrap();

      const type_bucket = types.find((service) => service.href.includes("/bucket"));
      if (!type_bucket) {
        setIsPreparing(false);
        setPrepareError("No bucket type is found");
        return;
      }
      dispatch(selectType(type_bucket.href));

      await dispatch(fetchDataItems());
      setIsPreparing(false);
      setPrepareError("");
      setPage("bucket");
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      setPrepareError(errorMessage);
      setIsPreparing(false);
    }
  }

  async function setCloudAccountName(value: string) {
    setPrepareError("");
    dispatch(clearReachError());
    dispatch(setCanReach(false));
    _setCloudAccountName(value);
    if (value) {
      const { absolute: cloudaccountLink } = parseLink(value, { kind: "cloudaccount" });
      dispatch(setCloudaccountLink(cloudaccountLink));
      setIsCheckingReach(true);
      await dispatch(checkCanReach());
      setIsCheckingReach(false);
    } else {
      dispatch(clearReachError());
    }
  }

  return {
    page,
    setPage,
    cloudAccountName,
    setCloudAccountName,
    isCheckingReach,
    isPreparing,
    prepareError,
    prepare,
  };
}
